import {
  AssetsSubformType,
  VariantType as AssetsSubformVariant,
} from '@/modules/assets/AssetsSubform/AssetsSubform';
import { SubformAsset } from '@/modules/assets/AssetsSubform/types';
import { Asset, NEW_ASSET_ID } from '@/modules/assets/types/asset';
import { decodeAssetTaxSitusFormValue } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import {
  AssetValuationV2ValuationReason,
  AssetValueV2OwnershipType,
  AugmentedCreateAssetStaticInput,
  AugmentedCreateAssetV2Input,
  AugmentedCreateAssetValuationV2Input,
  AugmentedUpdateAssetV2Input,
  CreateAssetV2Input,
  CreateAssetValueV2Input,
} from '@/types/schema';
import { AugmentedUpdateAssetValuationV2Input } from '@/types/schema';

// this function is responsible for mapping the frontend Asset concept to the variables
// required to update the Asset and AssetValue models in the backend.
// householdID is omitted because callers spread it in themselves alongside the
// rest of the create input.
function getAssetValuationPropsForMutation(
  asset: Asset,
  opts = { isUpdate: false }
): Omit<CreateAssetValueV2Input, 'householdID'> {
  switch (asset.valuationMethod) {
    case AssetValueV2OwnershipType.ShareBased:
      return {
        shareCount: asset.shareCount || null,
        shareValue: asset.sharePrice || null,
        ownershipType: asset.valuationMethod,
        ...(opts.isUpdate
          ? {
              clearOwnedValue: true,
              clearTotalValue: true,
              clearOwnedPercent: true,
            }
          : {}),
      };
    case AssetValueV2OwnershipType.ValueBased:
      return {
        ownedValue: asset.marketValue || null,
        ownershipType: asset.valuationMethod,
        ...(opts.isUpdate
          ? {
              clearShareCount: true,
              clearShareValue: true,
              clearTotalValue: true,
              clearOwnedPercent: true,
            }
          : {}),
      };
    case AssetValueV2OwnershipType.PercentBased:
      return {
        totalValue: asset.totalValue || null,
        ownedPercent: asset.ownedPercent || null,
        ownershipType: asset.valuationMethod,
        ...(opts.isUpdate
          ? {
              clearShareCount: true,
              clearShareValue: true,
              clearOwnedValue: true,
            }
          : {}),
      };
    default:
      throw new Error(`Unrecognized valuationMethod ${asset.valuationMethod}`);
  }
}

export function getAssetMutationUpdates(
  assets: (Asset | SubformAsset)[],
  householdId: string,
  isAssetSubstitution = false,
  // When omitted, situs is a no-op (AssetStatic.entityID is required at create time).
  entityId?: string
) {
  const newAssets: AugmentedCreateAssetV2Input[] = [];
  const existingAssetsUpdate: AugmentedUpdateAssetV2Input[] = [];
  const existingAssetsCreate: AugmentedCreateAssetV2Input[] = [];
  const removedAssetIds: string[] = [];

  assets.forEach((asset: SubformAsset | Asset) => {
    if (asset.doDelete) {
      removedAssetIds.push(asset.id);
      return;
    }

    // Replace AssetStatic when missing, renamed, or situs changed (keeps the FK 1:1 without a separate update mutation).
    const renamed =
      !!asset.assetStaticID &&
      asset.displayName !== asset.assetStaticDisplayName;
    const situsChanged =
      (asset.taxSitus ?? '') !== (asset._originalTaxSitus ?? '');
    const forceFreshStatic = !asset.assetStaticID || renamed || situsChanged;

    const reusedAssetStaticID = forceFreshStatic
      ? undefined
      : asset.assetStaticID;

    const assetDetails: CreateAssetV2Input = {
      displayName: asset.displayName,
      classID: asset.categoryId || '',
      qsbsEligibility: asset.qsbsEligibility,
      householdID: householdId,
      assetStaticID: reusedAssetStaticID,
    };

    const decodedSitus = decodeAssetTaxSitusFormValue(asset.taxSitus);
    const withAssetStatic: AugmentedCreateAssetStaticInput | undefined =
      forceFreshStatic && entityId
        ? {
            create: {
              name: asset.displayName,
              entityID: entityId,
              householdID: householdId,
              ...decodedSitus,
            },
          }
        : undefined;

    const assetValueCreate = getAssetValuationPropsForMutation(asset);
    const assetValueUpdate = getAssetValuationPropsForMutation(asset, {
      isUpdate: true,
    });

    if (asset.id.startsWith(NEW_ASSET_ID) || isAssetSubstitution) {
      newAssets.push({
        create: {
          ...assetDetails,
          householdID: householdId,
        },
        withAssetValue: {
          create: {
            ...assetValueCreate,
            householdID: householdId,
          },
        },
        withAssetStatic,
      });
    } else {
      // it's a little funny to return mutations for both `existingAssetsUpdate` and
      // `existingAssetsCreate`, but the expectation is that the serializers that consume this
      // set of possible mutation updates won't be using both of them, but rather will be using
      // one or the other depending on its own use case. for example, in order to update the
      // holdings, we would use `existingAssetsCreate`. to do the initial funding revaluation, we
      // would use existingAssetsUpdate.

      // this is intended for the scenario when you are creating a new asset value for an existing asset,
      // but also want to potentially update the asset details at the same time
      if (asset.valueId) {
        existingAssetsUpdate.push({
          id: asset.id,
          update: assetDetails,
          updateAssetValue: {
            id: asset.valueId,
            update: assetValueUpdate,
          },
        });
      }
      // this is intended for creating a net-new asset with a value
      existingAssetsCreate.push({
        create: {
          ...assetDetails,
          householdID: householdId,
        },
        withAssetValue: {
          create: {
            ...assetValueCreate,
            householdID: householdId,
          },
        },
        withAssetStatic,
      });
    }
  });

  return {
    newAssets,
    existingAssetsUpdate,
    existingAssetsCreate,
    removedAssetIds,
  };
}

export const serializers: Record<
  AssetsSubformVariant,
  (
    input: AssetsSubformType,
    householdId: string,
    entityId?: string
  ) =>
    | AugmentedUpdateAssetValuationV2Input
    | AugmentedCreateAssetValuationV2Input
> = {
  initialFundingRevaluation: (
    formValues,
    householdId,
    entityId
  ): AugmentedUpdateAssetValuationV2Input => {
    if (!formValues?.assets) {
      throw new Error('Trying to parse empty form');
    }

    const { newAssets, existingAssetsCreate } = getAssetMutationUpdates(
      formValues.assets,
      householdId,
      false,
      entityId
    );

    return {
      id: formValues.valuationId!,
      update: {
        clearAssets: true,
      },
      withAssets: [...newAssets, ...existingAssetsCreate],
    };
  },
  verifyInitialFunding: (
    formValues,
    householdId,
    entityId
  ): AugmentedUpdateAssetValuationV2Input => {
    if (!formValues?.assets || !formValues.valuationId) {
      throw new Error('Trying to parse empty form');
    }

    const { newAssets, existingAssetsCreate } = getAssetMutationUpdates(
      formValues.assets,
      householdId,
      false,
      entityId
    );

    return {
      id: formValues.valuationId,
      update: {
        effectiveDate: formValues.fundingDate,
        valuationReason: AssetValuationV2ValuationReason.InitialFunding,
        clearAssets: true,
      },
      withAssets: [...newAssets, ...existingAssetsCreate],
    };
  },
  structuringDesigner: (
    formValues,
    householdId,
    entityId
  ): AugmentedUpdateAssetValuationV2Input => {
    if (!formValues?.assets || !formValues.valuationId) {
      throw new Error('Trying to parse empty form');
    }

    const { newAssets, existingAssetsCreate } = getAssetMutationUpdates(
      formValues.assets,
      householdId,
      false,
      entityId
    );

    if (true as unknown) {
      throw new Error(
        'TODO: VALIDATE THAT THIS WORKS. THIS WAS PORTED BUT NOT VALIDATED.'
      );
    }

    return {
      id: formValues.valuationId,
      update: {
        clearAssets: true,
        effectiveDate: new Date(new Date(0).toISOString()),
      },
      withAssets: [...newAssets, ...existingAssetsCreate],
    };
  },
  editDesignSummary: (
    formValues,
    householdId,
    entityId
  ): AugmentedUpdateAssetValuationV2Input => {
    if (!formValues?.assets || !formValues.valuationId) {
      throw new Error('Trying to parse empty form');
    }

    const { newAssets, existingAssetsCreate } = getAssetMutationUpdates(
      formValues.assets,
      householdId,
      false,
      entityId
    );

    return {
      id: formValues.valuationId,
      update: {
        clearAssets: true,
      },
      withAssets: [...newAssets, ...existingAssetsCreate],
    };
  },
  updateHoldings: (
    formValues,
    householdId,
    entityId
  ): AugmentedCreateAssetValuationV2Input => {
    if (
      !formValues?.assets ||
      !formValues.valuationReason ||
      !formValues.valuationDate
    ) {
      throw new Error('Trying to parse empty form');
    }

    const isAssetSubstitution =
      formValues.valuationReason ===
      AssetValuationV2ValuationReason.GratAssetSubstitution;

    const { newAssets, existingAssetsCreate } = getAssetMutationUpdates(
      formValues.assets,
      householdId,
      isAssetSubstitution,
      entityId
    );

    return {
      create: {
        householdID: householdId,
        effectiveDate: formValues.valuationDate,
        valuationReason: formValues.valuationReason,
      },
      withAssets: [...newAssets, ...existingAssetsCreate],
    };
  },
};
